import { GraphQLRequest, ResponseResolver } from 'msw';
import { DEFAULT_ERROR_SIZE } from '../components/file-processes/file-process-errors';
import {
  AddUserMutationVariables,
  GetDirectoryQueryVariables,
  GetFileQueryVariables,
  GetProblemFilesQueryVariables,
  GetUserManagementDataQueryVariables,
  MoveFileMutationVariables,
  RenameFileMutationVariables,
  UserType,
} from './generated';
import {
  makeMockAppConfig,
  makeMockAvFileProcess,
  makeMockCedantDetails,
  makeMockFile,
  makeMockFileProcessErrors,
  makeMockGenericFileProcess,
  makeMockPiiFileProcess,
  makeMockProblematicFilesObject,
  makeMockRole,
  makeMockTreatyUser,
  makeMockTreatyUsers,
} from './graphql.mocks';
import { makeMockDirectory, makeMockStorageObjects } from './graphql.mocks';
import {
  GQLContextType,
  makeActionHistoryFilterOptionsMock,
  makeArchiveDirectoryMock,
  makeGetActivityFilterOptionsMock,
} from './msw-dev-handlers.generated';
import { makeGetAppConfigMock } from './msw-dev-handlers.generated';

/**
 * QUERIES
 */

makeGetAppConfigMock({
  appConfig: makeMockAppConfig({
    canManageUsers: true,
    canDownloadPiiReport: true,
    canViewProblemFiles: true,
  }),
});

makeGetActivityFilterOptionsMock({
  cedant: {
    __typename: 'CedantDetails',
    id: 'SYMETRA',
    name: 'Symetra',
    activityFilterOptions: {
      __typename: 'ActivityFilterOptions',
      users: [
        { __typename: 'FilterUserOption', id: 'user-1', name: 'Sumit Kumar' },
        { __typename: 'FilterUserOption', id: 'user-2', name: 'Vishal Kumar' },
      ],
      paths: [
        'Holdings/AllianzLife_ID_Holdings_20260731.xlsx',
        'Reports/ACPoA_Monthly_Report.pdf',
      ],
    },
  },
});

makeActionHistoryFilterOptionsMock({
  actionHistoryFilterOptions: {
    __typename: 'ActionHistoryFilterOptions',
    users: [
      { __typename: 'FilterUserOption', id: 'user-1', name: 'Sumit Kumar' },
      { __typename: 'FilterUserOption', id: 'user-2', name: 'Vishal Kumar' },
    ],
    actionTypes: ['Added user', 'Blocked user', 'Changed roles', 'Reset MFA'],
  },
});

export const handleGetDirectory: ResponseResolver<
  GraphQLRequest<GetDirectoryQueryVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  const { cedant, directoryId } = req.variables;

  return res(
    ctx.data({
      cedant: makeMockCedantDetails({
        id: cedant,
        directory: makeMockDirectory({
          id: directoryId || 'mock-dir-id',
          objects: makeMockStorageObjects(),
        }),
      }),
    })
  );
};

export const handleGetFile: ResponseResolver<
  GraphQLRequest<GetFileQueryVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  const { cedant, fileId, errorsPagination } = req.variables;
  const errorSize = errorsPagination?.limit || DEFAULT_ERROR_SIZE;

  return res(
    ctx.data({
      cedant: {
        id: cedant,
        name: 'Symetra',
        file: makeMockFile({
          id: fileId,
          canArchive: true,
          canDelete: true,
          canDownload: true,
          canPiiJustify: true,
          canPiiApprove: true,
          canRename: true,
          canMove: true,
          canRetryProcesses: true,
          processes: [
            makeMockGenericFileProcess({
              errors: makeMockFileProcessErrors({
                count: errorSize,
                totalCount: 9,
              }),
            }),
            makeMockPiiFileProcess(),
            makeMockAvFileProcess(),
          ],
        }),
      },
    })
  );
};

export const handleGetUserManagementData: ResponseResolver<
  GraphQLRequest<GetUserManagementDataQueryVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  const { searchParams } = req.variables;
  const userType = searchParams.userType || UserType.EXTERNAL_USER;

  return res(
    ctx.delay(1000),
    ctx.data({
      treatyUsers: makeMockTreatyUsers([
        makeMockTreatyUser({ type: userType }),
        makeMockTreatyUser({ type: userType }),
        makeMockTreatyUser({ type: userType }),
      ]),
      treaties: [
        makeMockCedantDetails(),
        makeMockCedantDetails(),
        makeMockCedantDetails(),
      ],
      roles: [
        makeMockRole({ id: 'allianz_us' }),
        makeMockRole({ id: 'kiku' }),
        makeMockRole({ id: 'symetra' }),
      ],
    })
  );
};

/**
 * MUTATIONS
 */

makeArchiveDirectoryMock({ archiveDirectory: true });

export const handleRenameFile: ResponseResolver<
  GraphQLRequest<RenameFileMutationVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  const { cedant, input } = req.variables;

  return res(
    ctx.delay(1000),
    ctx.data({
      renameFile: makeMockFile({
        cedant,
        id: input.fileID,
        name: input.newFileName,
        path: '',
      }),
    })
  );
};

export const handleMoveFile: ResponseResolver<
  GraphQLRequest<MoveFileMutationVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  const { cedant, input } = req.variables;

  return res(
    ctx.delay(1000),
    ctx.data({
      moveFile: makeMockFile({
        cedant,
        id: input.fileID,
      }),
    })
  );
};

export const handleAddUser: ResponseResolver<
  GraphQLRequest<AddUserMutationVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  const { input } = req.variables;

  return res(
    ctx.delay(1000),
    ctx.data({
      addUser: makeMockTreatyUser({
        name: input.name,
      }),
    })
  );
};

export const handleGetProblemFiles: ResponseResolver<
  GraphQLRequest<GetProblemFilesQueryVariables>,
  GQLContextType
> = async (req, res, ctx) => {
  return res(
    ctx.data({
      problematicFiles: makeMockProblematicFilesObject(),
    })
  );
};
